import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useOutletContext, useParams } from "react-router-dom";
import { get } from "../api";
import type { CustomerPage, Role } from "../api";
import { CategoryChip, StagePill } from "../Classification";
import CustomerSidebar from "./CustomerSidebar";

/**
 * The customer's header and tab bar, shared by every tab.
 *
 * A layout route rather than a header each tab renders for itself: the
 * customer is fetched once, and switching tabs does not remount the heading
 * or — the expensive one — throw away a report whose source material has
 * been loaded and refetch the lot.
 *
 * The sidebar sits outside everything that depends on the customer, so
 * switching from one to the next keeps it mounted -- its list, its scroll
 * position and its search box -- while only the right-hand side reloads.
 * That side is keyed on the customer, so no tab can briefly render the
 * previous customer's numbers under the new one's name.
 */
export default function CustomerLayout() {
  const { ref } = useParams<{ ref: string }>();
  const [customer, setCustomer] = useState<CustomerPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (keepShowing = false) => {
      if (!keepShowing) setCustomer(null);
      setError(null);
      void get<CustomerPage>(`/api/customers/${ref}`)
        .then(setCustomer)
        .catch((e) => setError(e.message));
    },
    [ref],
  );

  useEffect(() => load(), [load]);

  // Handed to the tabs so tracking an item on the Report tab updates the
  // count on the other one. Refetches without blanking the page, since the
  // header is still perfectly valid while the new number is in flight.
  const refresh = useCallback(() => load(true), [load]);

  const main = error ? (
    <>
      <div className="error">{error}</div>
      <NavLink to="/">Back to customers</NavLink>
    </>
  ) : !customer ? (
    <div className="empty">Loading…</div>
  ) : (
    <div key={ref}>
      <h1 className="titled">
        {customer.display_name}
        <StagePill stage={customer.stage} />
        <CategoryChip category={customer.category} />
      </h1>
      {customer.assigned.length > 0 && (
        <p className="sub">
          Assigned:{" "}
          {customer.assigned.map((u) => `${u.name} (${ROLE_LABEL[u.role]})`).join(", ")}
        </p>
      )}

      <div className="tabs">
        <NavLink end to={`/c/${ref}`} className={({ isActive }) => (isActive ? "tab on" : "tab")}>
          Overview
        </NavLink>
        <NavLink
          to={`/c/${ref}/report`}
          className={({ isActive }) => (isActive ? "tab on" : "tab")}
        >
          Report
        </NavLink>
        <NavLink
          to={`/c/${ref}/actions`}
          className={({ isActive }) => (isActive ? "tab on" : "tab")}
        >
          Action items
          {customer.open_actions > 0 && <span className="count">{customer.open_actions}</span>}
        </NavLink>
        <NavLink
          to={`/c/${ref}/metrics`}
          className={({ isActive }) => (isActive ? "tab on" : "tab")}
        >
          Metrics
        </NavLink>
        <NavLink
          to={`/c/${ref}/revenue`}
          className={({ isActive }) => (isActive ? "tab on" : "tab")}
        >
          Revenue
        </NavLink>
      </div>

      <Outlet context={{ customer, refresh } satisfies CustomerContext} />
    </div>
  );

  return (
    <div className="wrap wide cshell">
      <CustomerSidebar current={ref ?? ""} />
      <div className="cmain">{main}</div>
    </div>
  );
}

export const ROLE_LABEL: Record<Role, string> = { admin: "Admin", csm: "CSM" };

interface CustomerContext {
  customer: CustomerPage;
  /** Re-read the header, after a tab changed something it counts. */
  refresh: () => void;
}

/** The customer the layout already fetched, for whichever tab is showing. */
export const useCustomer = () => useOutletContext<CustomerContext>();
